import React from 'react';
import { FileTextIcon, UserIcon, BuildingIcon, CalendarIcon, ClockIcon, MapPinIcon, DollarSignIcon, TruckIcon, TagIcon } from 'lucide-react';
export function QuotePreview({
  quoteData,
  quoteType,
  quoteNumber
}) {
  // Valores por defecto para evitar errores
  const customerInfo = quoteData.customerInfo || {};
  const shipping = quoteData.shipping || {};
  const currency = shipping.currency || quoteData.currency || '$';
  const pivots = quoteData.pivots || [];
  const parts = quoteData.parts || [];
  const items = quoteData.items || [];
  const issueDate = quoteData.issueDate || quoteData.date || 'Unknown date';
  const expiryDate = quoteData.expiryDate || 'Unknown';
  return <div className="text-gray-800" data-id="element-4865">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 pb-8 border-b border-gray-200" data-id="element-4866">
        <div data-id="element-4867">
          <div className="flex items-center mb-2" data-id="element-4868">
            <div className="p-2 mr-3 bg-blue-600 rounded-lg" data-id="element-4869">
              <FileTextIcon size={20} className="text-white" data-id="element-4870" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800" data-id="element-4871">
              Quote #{quoteNumber}
            </h1>
          </div>
          <div className="text-gray-600" data-id="element-4872">
            {quoteType} Quote • Created by {quoteData.quotedBy || 'Unknown'}
          </div>
          <div className="mt-4 flex flex-wrap gap-4" data-id="element-4873">
            <div className="flex items-center text-gray-600" data-id="element-4874">
              <CalendarIcon size={16} className="mr-1" data-id="element-4875" />
              Issue Date: {issueDate}
            </div>
            <div className="flex items-center text-gray-600" data-id="element-4876">
              <ClockIcon size={16} className="mr-1" data-id="element-4877" />
              Expiry Date: {expiryDate}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end" data-id="element-4878">
          <div className="text-3xl font-bold text-blue-600" data-id="element-4879">
            {currency}
            {(quoteData.amount || 0).toLocaleString(undefined, {
            maximumFractionDigits: 2
          })}
          </div>
          {quoteData.status && <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm" data-id="element-4880">
              <TagIcon size={14} className="mr-1" data-id="element-4881" />
              {quoteData.status.charAt(0).toUpperCase() + quoteData.status.slice(1)}
            </div>}
        </div>
      </div>

      {/* Customer Information */}
      <div className="mb-8" data-id="element-4882">
        <h2 className="text-xl font-bold mb-4" data-id="element-4883">Customer Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-4 rounded-lg" data-id="element-4884">
          <div data-id="element-4885">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4886">Bill To</h3>
            <div className="flex items-start" data-id="element-4887">
              {customerInfo.clientType === 'dealer' || quoteData.clientType === 'dealer' ? <BuildingIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4888" /> : <UserIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4889" />}
              <div data-id="element-4890">
                <div className="font-medium" data-id="element-4891">
                  {customerInfo.billToCompany || customerInfo.dealerName || quoteData.dealerName || 'Not specified'}
                </div>
                <div className="text-gray-600" data-id="element-4892">
                  {customerInfo.billToAddress || 'No address provided'}
                </div>
                <div className="text-gray-600" data-id="element-4893">
                  {customerInfo.billToCountry || quoteData.country || 'Country not specified'}
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-4894">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4895">Ship To</h3>
            <div className="flex items-start" data-id="element-4896">
              <MapPinIcon size={18} className="mr-2 text-gray-400 mt-0.5" data-id="element-4897" />
              <div data-id="element-4898">
                <div className="font-medium" data-id="element-4899">
                  {customerInfo.shipToCompany || customerInfo.dealerName || quoteData.dealerName || 'Same as billing'}
                </div>
                <div className="text-gray-600" data-id="element-4900">
                  {customerInfo.shipToAddress || 'No address provided'}
                </div>
                <div className="text-gray-600" data-id="element-4901">
                  {customerInfo.shipToCountry || customerInfo.billToCountry || quoteData.country || 'Country not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Items */}
      <div className="mb-8" data-id="element-4902">
        <h2 className="text-xl font-bold mb-4" data-id="element-4903">Quote Items</h2>
        <div className="overflow-x-auto" data-id="element-4904">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-4905">
            <thead className="bg-gray-50" data-id="element-4906">
              <tr data-id="element-4907">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4908">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4909">
                  Quantity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4910">
                  Unit Price
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4911">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-4912">
              {/* Render pivot items if available */}
              {pivots.length > 0 && pivots.map((pivot, index) => <tr key={`pivot-${index}`} data-id="element-4913">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4914">
                      <div className="font-medium text-gray-900" data-id="element-4915">
                        {pivot.name}
                      </div>
                      <div className="text-gray-500" data-id="element-4916">{pivot.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4917">
                      1
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4918">
                      {currency}
                      {pivot.price.toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4919">
                      {currency}
                      {pivot.price.toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* Render parts items if available */}
              {parts.length > 0 && parts.map((part, index) => <tr key={`part-${index}`} data-id="element-4920">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4921">
                      <div className="font-medium text-gray-900" data-id="element-4922">
                        {part.name || part.partNumber}
                      </div>
                      <div className="text-gray-500" data-id="element-4923">{part.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4924">
                      {part.quantity || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4925">
                      {currency}
                      {(part.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4926">
                      {currency}
                      {((part.price || 0) * (part.quantity || 1)).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* Render project items if available */}
              {items.length > 0 && items.map((item, index) => <tr key={`item-${index}`} data-id="element-4927">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4928">
                      <div className="font-medium text-gray-900" data-id="element-4929">
                        {item.name}
                      </div>
                      <div className="text-gray-500" data-id="element-4930">{item.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4931">
                      {item.quantity || 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-500" data-id="element-4932">
                      {currency}
                      {(item.price || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-900 font-medium" data-id="element-4933">
                      {currency}
                      {((item.price || 0) * (item.quantity || 1)).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                    </td>
                  </tr>)}
              {/* If no items are available, show a placeholder */}
              {pivots.length === 0 && parts.length === 0 && items.length === 0 && <tr data-id="element-4934">
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500" data-id="element-4935">
                      No items specified in this quote
                    </td>
                  </tr>}
              {/* Total row */}
              <tr className="bg-gray-50" data-id="element-4936">
                <td colSpan={3} className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-700" data-id="element-4937">
                  Total Amount:
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-blue-600 font-bold" data-id="element-4938">
                  {currency}
                  {(quoteData.amount || 0).toLocaleString(undefined, {
                  maximumFractionDigits: 2
                })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Shipping & Terms */}
      <div className="mb-8" data-id="element-4939">
        <h2 className="text-xl font-bold mb-4" data-id="element-4940">Shipping & Terms</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-lg" data-id="element-4941">
          <div data-id="element-4942">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4943">
              Payment Terms
            </h3>
            <div className="flex items-center" data-id="element-4944">
              <DollarSignIcon size={18} className="mr-2 text-gray-400" data-id="element-4945" />
              <div className="font-medium" data-id="element-4946">
                {shipping.paymentTerms || 'Not specified'}
              </div>
            </div>
          </div>
          <div data-id="element-4947">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4948">Incoterm</h3>
            <div className="flex items-center" data-id="element-4949">
              <TruckIcon size={18} className="mr-2 text-gray-400" data-id="element-4950" />
              <div className="font-medium" data-id="element-4951">
                {shipping.incoterm || 'Not specified'}
              </div>
            </div>
          </div>
          <div data-id="element-4952">
            <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4953">Currency</h3>
            <div className="flex items-center" data-id="element-4954">
              <DollarSignIcon size={18} className="mr-2 text-gray-400" data-id="element-4955" />
              <div className="font-medium" data-id="element-4956">{currency}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes & Terms */}
      <div className="mb-8" data-id="element-4957">
        <h2 className="text-xl font-bold mb-4" data-id="element-4958">Notes & Terms</h2>
        <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4959">
          <p className="text-gray-600" data-id="element-4960">
            {shipping.notes || quoteData.notes || 'This quote is valid until the expiry date mentioned above. Please contact us if you have any questions.'}
          </p>
        </div>
      </div>
    </div>;
}